/**
 * Full-screen voice chat modal
 *
 * Provides immersive voice conversation experience with:
 * - Animated voice orb
 * - Scrollable conversation history
 * - Real-time transcripts
 * - Interrupt support
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PhoneOff, Calendar, ChevronLeft, ChevronRight, ExternalLink, SlidersHorizontal, Square, X } from 'lucide-react';
import { VoiceOrb, OrbState } from './VoiceOrb';
import CalendarWidget from './CalendarWidget';
import { ConversationHeaderBrand } from './ConversationHeaderBrand';
import {
  useRealtimeVoice,
  RealtimeMessage,
  RealtimeVoiceAction,
  GateStats,
  GateConfig,
  GateRuntimeConfig,
  AudioPlaybackConfig,
  TranscriptTuningConfig,
  EffectiveVoiceConfig,
  DEFAULT_GATE_CONFIG,
  DEFAULT_AUDIO_PLAYBACK_CONFIG,
  DEFAULT_TRANSCRIPT_TUNING_CONFIG,
} from '../hooks/useRealtimeVoice';
import {
  attachAvatar,
  configureAvatarAudioPacing,
  getAvatarAudioPacingConfig,
  getDefaultAvatarAudioPacingConfig,
  getAvatarSessionStatus,
  interruptAvatar,
  sendAvatarAudio,
  setAvatarTrackingContext,
  subscribeAvatarSpeakingState,
  startAvatarSession,
  stopAvatarSession,
} from '../features/liveavatar';
import { AvatarAudioPacingConfig } from '../features/liveavatar/types';
import type { DebugProfile } from '../hooks/useAdminApi';

const AVATAR_MIC_RELEASE_DELAY_MS = 350;
const AVATAR_STARTUP_GATE_TIMEOUT_MS = 3500;
const AVATAR_FIRST_FRAME_WAIT_MS = 1800;
const INITIAL_GREETING_PLAYBACK_RELEASE_MS = 700;
const INITIAL_GREETING_PLAYBACK_LOCK_FALLBACK_MS = 12000;
const MAX_VOICE_SESSION_DURATION_MS = 30 * 60 * 1000;
const VOICE_DEBUG_SETTINGS_STORAGE_KEY = 'voice_debug_settings_v2';

const getNowMs = (): number => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
};

const hasAvatarVideoFrame = (video: HTMLVideoElement): boolean => {
  return (
    video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
    video.videoWidth > 0 &&
    video.videoHeight > 0
  );
};

const waitForAvatarFirstFrame = (
  video: HTMLVideoElement,
  timeoutMs: number,
): Promise<boolean> => {
  if (hasAvatarVideoFrame(video)) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let animationFrameId: number | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      video.removeEventListener('loadeddata', checkReady);
      video.removeEventListener('canplay', checkReady);
      video.removeEventListener('playing', checkReady);
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (animationFrameId !== null && typeof window !== 'undefined' && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const finish = (ready: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve(ready);
    };

    function checkReady() {
      if (hasAvatarVideoFrame(video)) {
        finish(true);
      }
    }

    const poll = () => {
      if (settled) {
        return;
      }
      if (hasAvatarVideoFrame(video)) {
        finish(true);
        return;
      }
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        animationFrameId = window.requestAnimationFrame(poll);
        return;
      }
      pollTimer = setTimeout(poll, 50);
    };

    video.addEventListener('loadeddata', checkReady);
    video.addEventListener('canplay', checkReady);
    video.addEventListener('playing', checkReady);
    timer = setTimeout(() => finish(false), timeoutMs);
    poll();
  });
};

const getAvatarStartupErrorMessage = (err: unknown): string => {
  const message = err instanceof Error ? err.message : String(err || '');
  if (/no credits available/i.test(message)) {
    return 'LiveAvatar-Credits sind aufgebraucht. Bitte Credits im LiveAvatar-Konto nachladen oder den Sandbox-Modus verwenden.';
  }

  return message || 'Avatar session failed to start';
};

const getLinkCtaLabel = (label?: string): string => {
  const normalized = (label || '').replace(/^Link gesendet:\s*/i, '').trim();
  return normalized || 'Link';
};

const DEFAULT_DEBUG_GATE_CONFIG: GateRuntimeConfig = DEFAULT_GATE_CONFIG;

const DEFAULT_DEBUG_PLAYBACK_CONFIG: AudioPlaybackConfig = DEFAULT_AUDIO_PLAYBACK_CONFIG;

const DEFAULT_DEBUG_AVATAR_PACING_CONFIG: AvatarAudioPacingConfig = {
  minChunkSendMs: 70,
  maxChunkSendMs: 220,
  schedulerResetDriftMs: 400,
};

const DEFAULT_DEBUG_TRANSCRIPT_TUNING_CONFIG: TranscriptTuningConfig = DEFAULT_TRANSCRIPT_TUNING_CONFIG;

export interface VoiceDebugSettings {
  gate: GateRuntimeConfig;
  playback: AudioPlaybackConfig;
  avatarPacing: AvatarAudioPacingConfig;
  transcriptTuning: TranscriptTuningConfig;
  muteDuringSpeaking: boolean;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const saveVoiceDebugSettings = (settings: VoiceDebugSettings): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(VOICE_DEBUG_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (_) {
    // ignore storage errors (private mode/quota)
  }
};

const loadVoiceDebugSettings = (): Partial<VoiceDebugSettings> => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(VOICE_DEBUG_SETTINGS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
};

const voiceDebugSettingsFromProfile = (profile?: DebugProfile | null): Partial<VoiceDebugSettings> | null => {
  const settings = profile?.settings;
  if (!settings) {
    return null;
  }
  return {
    gate: settings.gate as GateRuntimeConfig | undefined,
    playback: settings.playback as AudioPlaybackConfig | undefined,
    avatarPacing: settings.avatarPacing as AvatarAudioPacingConfig | undefined,
    transcriptTuning: settings.transcriptTuning as TranscriptTuningConfig | undefined,
    muteDuringSpeaking:
      typeof settings.muteDuringSpeaking === 'boolean'
        ? settings.muteDuringSpeaking
        : undefined,
  };
};

const normalizeTranscriptForComparison = (text: string): string =>
  text.trim().replace(/\s+/g, ' ').toLowerCase();

// ── Voice Debug Panel ──────────────────────────────────────────────────────────

interface SliderRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: (v: number) => string;
  onChange: (v: number) => void;
}

const SliderRow: React.FC<SliderRowProps> = ({ label, value, min, max, step, display, onChange }) => (
  <div className="flex items-center gap-2">
    <span className="w-28 shrink-0 text-gray-400 text-xs font-mono">{label}</span>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      className="flex-1 accent-green-400 h-1"
    />
    <span className="w-14 text-right text-green-300 text-xs font-mono">{display(value)}</span>
  </div>
);

interface SelectOption {
  value: string;
  label: string;
}

interface SelectRowProps {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
}

const SelectRow: React.FC<SelectRowProps> = ({ label, value, options, onChange }) => (
  <div className="flex items-center gap-2">
    <span className="w-28 shrink-0 text-gray-400 text-xs font-mono">{label}</span>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="flex-1 rounded border border-gray-700 bg-gray-900 px-2 py-1 text-xs font-mono text-gray-100 focus:border-green-400 focus:outline-none"
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

interface VoiceDebugPanelProps {
  getGateStats: () => GateStats | null;
  configureGate: (opts: GateConfig) => void;
  getGateConfig: () => GateRuntimeConfig;
  getDefaultGateConfig: () => GateRuntimeConfig;
  configurePlayback: (opts: Partial<AudioPlaybackConfig>) => void;
  getPlaybackConfig: () => AudioPlaybackConfig;
  getDefaultPlaybackConfig: () => AudioPlaybackConfig;
  configureAvatarPacing: (opts: Partial<AvatarAudioPacingConfig>) => void;
  getAvatarPacing: () => AvatarAudioPacingConfig;
  getDefaultAvatarPacing: () => AvatarAudioPacingConfig;
  configureTranscriptTuning: (opts: Partial<TranscriptTuningConfig>) => void;
  getTranscriptTuningConfig: () => TranscriptTuningConfig;
  getDefaultTranscriptTuningConfig: () => TranscriptTuningConfig;
  getEffectiveVoiceConfig: () => EffectiveVoiceConfig | null;
  getMuteDuringSpeaking: () => boolean;
  setMuteDuringSpeaking: (val: boolean) => void;
  onVoiceInterruptEnabledChange: (enabled: boolean) => void;
  debugProfileName?: string;
  onSaveDebugProfileSettings?: (settings: VoiceDebugSettings) => Promise<boolean> | boolean;
  onClose: () => void;
}

const VoiceDebugPanel: React.FC<VoiceDebugPanelProps> = ({
  getGateStats,
  configureGate,
  getGateConfig,
  getDefaultGateConfig,
  configurePlayback,
  getPlaybackConfig,
  getDefaultPlaybackConfig,
  configureAvatarPacing,
  getAvatarPacing,
  getDefaultAvatarPacing,
  configureTranscriptTuning,
  getTranscriptTuningConfig,
  getDefaultTranscriptTuningConfig,
  getEffectiveVoiceConfig,
  getMuteDuringSpeaking,
  setMuteDuringSpeaking,
  onVoiceInterruptEnabledChange,
  debugProfileName,
  onSaveDebugProfileSettings,
  onClose,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const rmsRef = useRef<HTMLSpanElement>(null);
  const nfRef = useRef<HTMLSpanElement>(null);
  const thrRef = useRef<HTMLSpanElement>(null);
  const statusRef = useRef<HTMLSpanElement>(null);

  const gateDefaults = useRef<GateRuntimeConfig>(getDefaultGateConfig());
  const playbackDefaults = useRef<AudioPlaybackConfig>(getDefaultPlaybackConfig());
  const avatarPacingDefaults = useRef<AvatarAudioPacingConfig>(getDefaultAvatarPacing());
  const transcriptDefaults = useRef<TranscriptTuningConfig>(getDefaultTranscriptTuningConfig());

  const [minThreshold, setMinThreshold] = useState(gateDefaults.current.minThreshold);
  const [multiplier, setMultiplier] = useState(gateDefaults.current.thresholdMultiplier);
  const [hangover, setHangover] = useState(gateDefaults.current.hangoverMs);
  const [preRoll, setPreRoll] = useState(gateDefaults.current.preRollMs);
  const [gateEnabled, setGateEnabled] = useState(gateDefaults.current.enabled);
  const [playbackLeadMs, setPlaybackLeadMs] = useState(
    Math.round(playbackDefaults.current.minLeadTimeSeconds * 1000),
  );
  const [playbackTailMs, setPlaybackTailMs] = useState(
    Math.round(playbackDefaults.current.pendingEpsilonSeconds * 1000),
  );
  const [avatarMinChunkMs, setAvatarMinChunkMs] = useState(avatarPacingDefaults.current.minChunkSendMs);
  const [avatarMaxChunkMs, setAvatarMaxChunkMs] = useState(avatarPacingDefaults.current.maxChunkSendMs);
  const [avatarResetDriftMs, setAvatarResetDriftMs] = useState(
    avatarPacingDefaults.current.schedulerResetDriftMs,
  );
  const [transcriptionDelay, setTranscriptionDelay] = useState<TranscriptTuningConfig['transcriptionDelay']>(
    transcriptDefaults.current.transcriptionDelay,
  );
  const [noiseReductionType, setNoiseReductionType] = useState<TranscriptTuningConfig['noiseReductionType']>(
    transcriptDefaults.current.noiseReductionType,
  );
  const [turnDetectionType, setTurnDetectionType] = useState<TranscriptTuningConfig['turnDetectionType']>(
    transcriptDefaults.current.turnDetectionType,
  );
  const [vadEagerness, setVadEagerness] = useState<TranscriptTuningConfig['vadEagerness']>(
    transcriptDefaults.current.vadEagerness,
  );
  const [speechInterruptDebounceMs, setSpeechInterruptDebounceMs] = useState(
    transcriptDefaults.current.speechInterruptDebounceMs,
  );
  const [minValidSpeechMs, setMinValidSpeechMs] = useState(transcriptDefaults.current.minValidSpeechMs);
  const [maxShortUtteranceChars, setMaxShortUtteranceChars] = useState(
    transcriptDefaults.current.maxShortUtteranceChars,
  );
  const [maxPlausibleCharsPerSec, setMaxPlausibleCharsPerSec] = useState(
    transcriptDefaults.current.maxPlausibleCharsPerSec,
  );
  const [activeTab, setActiveTab] = useState<'audio' | 'transcript' | 'effective'>('audio');
  const [muteDuring, setMuteDuring] = useState(() => getMuteDuringSpeaking());
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const transcriptDelayOptions = [
    { value: 'minimal', label: 'Minimal' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'xhigh', label: 'XHigh' },
  ];
  const noiseReductionOptions = [
    { value: 'near_field', label: 'Near Field' },
    { value: 'far_field', label: 'Far Field' },
    { value: 'off', label: 'Off' },
  ];
  const turnDetectionOptions = [
    { value: 'semantic_vad', label: 'Semantic VAD' },
    { value: 'server_vad', label: 'Server VAD' },
    { value: 'off', label: 'Off' },
  ];
  const vadEagernessOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'auto', label: 'Auto' },
  ];

  // Drag state
  const [pos, setPos] = useState({ top: 64, right: 12 });
  const dragRef = useRef<{ startX: number; startY: number; startTop: number; startLeft: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const seededRef = useRef(false);

  useEffect(() => {
    const SCALE = 0.12;

    const draw = () => {
      const canvas = canvasRef.current;
      const stats = getGateStats();

      if (canvas && stats) {
        const ctx = canvas.getContext('2d')!;
        const w = canvas.width;
        const h = canvas.height;
        const toX = (v: number) => Math.min(w - 1, (v / SCALE) * w);

        ctx.fillStyle = '#0f172a';
        ctx.fillRect(0, 0, w, h);

        const passing = !stats.gateEnabled || stats.gateOpen;
        const rmsW = toX(stats.rms);
        if (rmsW > 0) {
          const grad = ctx.createLinearGradient(0, 0, rmsW, 0);
          grad.addColorStop(0, passing ? '#16a34a' : '#374151');
          grad.addColorStop(1, passing ? '#86efac' : '#4b5563');
          ctx.fillStyle = grad;
          ctx.fillRect(0, 4, rmsW, h - 8);
        }

        const nfX = toX(stats.noiseFloor);
        ctx.strokeStyle = '#f59e0b';
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(nfX, 0); ctx.lineTo(nfX, h); ctx.stroke();

        if (stats.gateEnabled) {
          const thrX = toX(stats.threshold);
          ctx.strokeStyle = '#ef4444';
          ctx.lineWidth = 2;
          ctx.beginPath(); ctx.moveTo(thrX, 0); ctx.lineTo(thrX, h); ctx.stroke();
        }
      }

      // Update value cells via refs
      if (stats) {
        if (rmsRef.current) rmsRef.current.textContent = stats.rms.toFixed(4);
        if (nfRef.current) nfRef.current.textContent = stats.noiseFloor.toFixed(4);
        if (thrRef.current) thrRef.current.textContent = stats.gateEnabled ? stats.threshold.toFixed(4) : '—';
        if (statusRef.current) {
          if (!stats.gateEnabled) {
            statusRef.current.textContent = 'all audio passes';
            statusRef.current.style.color = '#93c5fd';
          } else if (stats.gateOpen) {
            statusRef.current.textContent = 'passing speech';
            statusRef.current.style.color = '#86efac';
          } else {
            statusRef.current.textContent = 'sending silence';
            statusRef.current.style.color = '#6b7280';
          }
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [getGateStats]);

  useEffect(() => {
    if (seededRef.current) {
      return;
    }
    seededRef.current = true;

    const gate = getGateConfig();
    setMinThreshold(gate.minThreshold);
    setMultiplier(gate.thresholdMultiplier);
    setHangover(gate.hangoverMs);
    setPreRoll(gate.preRollMs);
    setGateEnabled(gate.enabled);

    const playback = getPlaybackConfig();
    setPlaybackLeadMs(Math.round(playback.minLeadTimeSeconds * 1000));
    setPlaybackTailMs(Math.round(playback.pendingEpsilonSeconds * 1000));

    const pacing = getAvatarPacing();
    setAvatarMinChunkMs(pacing.minChunkSendMs);
    setAvatarMaxChunkMs(pacing.maxChunkSendMs);
    setAvatarResetDriftMs(pacing.schedulerResetDriftMs);

    const transcriptTuning = getTranscriptTuningConfig();
    setTranscriptionDelay(transcriptTuning.transcriptionDelay);
    setNoiseReductionType(transcriptTuning.noiseReductionType);
    setTurnDetectionType(transcriptTuning.turnDetectionType);
    setVadEagerness(transcriptTuning.vadEagerness);
    setSpeechInterruptDebounceMs(transcriptTuning.speechInterruptDebounceMs);
    setMinValidSpeechMs(transcriptTuning.minValidSpeechMs);
    setMaxShortUtteranceChars(transcriptTuning.maxShortUtteranceChars);
    setMaxPlausibleCharsPerSec(transcriptTuning.maxPlausibleCharsPerSec);
  }, [getGateConfig, getPlaybackConfig, getAvatarPacing, getTranscriptTuningConfig]);

  // Drag handlers
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startTop: rect.top, startLeft: rect.left };
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPos({ top: dragRef.current.startTop + dy, right: -1 }); // switch to left-based once dragged
      if (panelRef.current) {
        panelRef.current.style.left = `${dragRef.current.startLeft + dx}px`;
        panelRef.current.style.top = `${dragRef.current.startTop + dy}px`;
        panelRef.current.style.right = 'auto';
      }
    };
    const onUp = () => { dragRef.current = null; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, []);

  const handleGateToggle = () => {
    const next = !gateEnabled;
    setGateEnabled(next);
    configureGate({ enabled: next });
    setSaveState('idle');
  };

  const handleVoiceInterruptToggle = () => {
    const nextVoiceInterruptEnabled = muteDuring;
    const nextMuteDuringSpeaking = !nextVoiceInterruptEnabled;

    setMuteDuring(nextMuteDuringSpeaking);
    setMuteDuringSpeaking(nextMuteDuringSpeaking);
    onVoiceInterruptEnabledChange(nextVoiceInterruptEnabled);

    setSaveState('idle');
  };

  const getCurrentSettings = (): VoiceDebugSettings => ({
    gate: getGateConfig(),
    playback: getPlaybackConfig(),
    avatarPacing: getAvatarPacing(),
    transcriptTuning: getTranscriptTuningConfig(),
    muteDuringSpeaking: getMuteDuringSpeaking(),
  });

  const handleSaveSettings = async () => {
    const settings = getCurrentSettings();
    saveVoiceDebugSettings(settings);
    if (!onSaveDebugProfileSettings) {
      setSaveState('saved');
      return;
    }
    setSaveState('saving');
    const ok = await onSaveDebugProfileSettings(settings);
    setSaveState(ok ? 'saved' : 'failed');
  };

  const handleSaveLocalSettings = () => {
    const settings: VoiceDebugSettings = {
      gate: getGateConfig(),
      playback: getPlaybackConfig(),
      avatarPacing: getAvatarPacing(),
      transcriptTuning: getTranscriptTuningConfig(),
      muteDuringSpeaking: getMuteDuringSpeaking(),
    };
    saveVoiceDebugSettings(settings);
    setSaveState('saved');
  };

  const handleRestoreDefaults = () => {
    const defaultGate = getDefaultGateConfig();
    configureGate(defaultGate);
    setGateEnabled(defaultGate.enabled);
    setMinThreshold(defaultGate.minThreshold);
    setMultiplier(defaultGate.thresholdMultiplier);
    setHangover(defaultGate.hangoverMs);
    setPreRoll(defaultGate.preRollMs);

    const defaultPlayback = getDefaultPlaybackConfig();
    configurePlayback(defaultPlayback);
    setPlaybackLeadMs(Math.round(defaultPlayback.minLeadTimeSeconds * 1000));
    setPlaybackTailMs(Math.round(defaultPlayback.pendingEpsilonSeconds * 1000));

    const defaultPacing = getDefaultAvatarPacing();
    configureAvatarPacing(defaultPacing);
    setAvatarMinChunkMs(defaultPacing.minChunkSendMs);
    setAvatarMaxChunkMs(defaultPacing.maxChunkSendMs);
    setAvatarResetDriftMs(defaultPacing.schedulerResetDriftMs);

    const defaultTranscript = getDefaultTranscriptTuningConfig();
    configureTranscriptTuning(defaultTranscript);
    setTranscriptionDelay(defaultTranscript.transcriptionDelay);
    setNoiseReductionType(defaultTranscript.noiseReductionType);
    setTurnDetectionType(defaultTranscript.turnDetectionType);
    setVadEagerness(defaultTranscript.vadEagerness);
    setSpeechInterruptDebounceMs(defaultTranscript.speechInterruptDebounceMs);
    setMinValidSpeechMs(defaultTranscript.minValidSpeechMs);
    setMaxShortUtteranceChars(defaultTranscript.maxShortUtteranceChars);
    setMaxPlausibleCharsPerSec(defaultTranscript.maxPlausibleCharsPerSec);

    setMuteDuring(false);
    setMuteDuringSpeaking(false);
    onVoiceInterruptEnabledChange(true);
    setSaveState('idle');
  };

  const effectiveConfig = getEffectiveVoiceConfig();
  const frontendEffectiveConfig = {
    gate: getGateConfig(),
    playback: getPlaybackConfig(),
    avatarPacing: getAvatarPacing(),
    transcriptTuningDesired: getTranscriptTuningConfig(),
    muteDuringSpeaking: muteDuring,
    voiceInterruptEnabled: !muteDuring,
    localStorageKey: VOICE_DEBUG_SETTINGS_STORAGE_KEY,
    hasPersistedDebugSettings:
      typeof window !== 'undefined' && window.localStorage.getItem(VOICE_DEBUG_SETTINGS_STORAGE_KEY) !== null,
  };
  const effectiveConfigJson = JSON.stringify(
    {
      backendConfirmed: effectiveConfig,
      frontendRuntime: frontendEffectiveConfig,
    },
    null,
    2,
  );

  return (
    <div
      ref={panelRef}
      className="fixed z-[200] rounded-xl bg-gray-950 border border-gray-700 shadow-2xl text-white select-none"
      style={{ top: pos.top, right: 12, width: 420 }}
    >
      {/* Header — drag handle */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b border-gray-800 cursor-grab active:cursor-grabbing"
        onMouseDown={onMouseDown}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-mono font-semibold text-gray-400 tracking-wider shrink-0">VOICE DEBUG</span>
          <span ref={statusRef} className="text-xs font-mono truncate" style={{ color: '#6b7280' }}>—</span>
        </div>
        <button onClick={onClose} className="text-gray-600 hover:text-gray-300 text-sm leading-none ml-2 shrink-0">✕</button>
      </div>

      <div className="p-3 space-y-3">
        <div className="flex gap-2">
          {debugProfileName && (
            <div className="flex-1 truncate rounded bg-gray-900 px-2 py-1 text-[10px] font-mono text-gray-400">
              PROFIL: {debugProfileName}
            </div>
          )}
          <button
            onClick={() => setActiveTab('audio')}
            className={`flex-1 rounded py-1 text-xs font-mono font-semibold transition-colors ${
              activeTab === 'audio'
                ? 'bg-green-900 text-green-200 hover:bg-green-800'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            AUDIO
          </button>
          <button
            onClick={() => setActiveTab('transcript')}
            className={`flex-1 rounded py-1 text-xs font-mono font-semibold transition-colors ${
              activeTab === 'transcript'
                ? 'bg-green-900 text-green-200 hover:bg-green-800'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            TRANSKRIPT
          </button>
          <button
            onClick={() => setActiveTab('effective')}
            className={`flex-1 rounded py-1 text-xs font-mono font-semibold transition-colors ${
              activeTab === 'effective'
                ? 'bg-green-900 text-green-200 hover:bg-green-800'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            EFFECTIVE
          </button>
        </div>

        {activeTab === 'audio' && (
          <>
            {/* Toggles */}
            <div className="flex gap-2">
              <button
                onClick={handleGateToggle}
                className={`flex-1 py-1 rounded text-xs font-mono font-semibold transition-colors ${gateEnabled ? 'bg-green-900 text-green-200 hover:bg-green-800' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
              >
                GATE {gateEnabled ? 'ON' : 'OFF'}
              </button>
              <button
                onClick={handleVoiceInterruptToggle}
                className={`flex-1 py-1 rounded text-xs font-mono font-semibold transition-colors ${!muteDuring ? 'bg-orange-900 text-orange-200 hover:bg-orange-800' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
              >
                VOICE INTERRUPT {!muteDuring ? 'ON' : 'OFF'}
              </button>
            </div>

            {/* Meter canvas */}
            <canvas
              ref={canvasRef}
              width={240}
              height={36}
              className="w-full rounded block"
              style={{ background: '#0f172a' }}
            />

            {/* Values table */}
            <table className="w-full text-xs font-mono border-collapse">
              <tbody>
                <tr>
                  <td className="text-gray-500 pr-2 py-0.5">RMS (loudness)</td>
                  <td className="text-right">
                    <span ref={rmsRef} className="text-white">—</span>
                  </td>
                  <td className="pl-2 text-gray-700 text-right">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                  </td>
                </tr>
                <tr>
                  <td className="text-gray-500 pr-2 py-0.5">Noise floor</td>
                  <td className="text-right">
                    <span ref={nfRef} className="text-amber-400">—</span>
                  </td>
                  <td className="pl-2 text-right">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                  </td>
                </tr>
                <tr>
                  <td className="text-gray-500 pr-2 py-0.5">Threshold</td>
                  <td className="text-right">
                    <span ref={thrRef} className="text-red-400">—</span>
                  </td>
                  <td className="pl-2 text-right">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                  </td>
                </tr>
              </tbody>
            </table>

            <details className="rounded border border-gray-800 bg-gray-900/70 px-2 py-1">
              <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-wide text-gray-400">
                Tuning Guide
              </summary>
              <div className="mt-2 space-y-1 text-[10px] font-mono text-gray-300 leading-relaxed">
                <div>
                  <span className="text-gray-500">Stottern/Pausen:</span>{' '}
                  Lead buffer hoch (z. B. +10ms), Tail epsilon hoch, Chunk min/max leicht hoch.
                </div>
                <div>
                  <span className="text-gray-500">Roboter/abgehackt:</span>{' '}
                  Lead/Tail hoch, Reset drift auf 400-600ms.
                </div>
                <div>
                  <span className="text-gray-500">Zu viele Fehl-Interrupts:</span>{' '}
                  VOICE INTERRUPT OFF lassen, Min threshold oder Multiplier hoch, Hangover eher nicht zu hoch.
                </div>
                <div>
                  <span className="text-gray-500">Wörter werden abgeschnitten:</span>{' '}
                  Pre-roll hoch (200-350ms), Hangover hoch (350-550ms).
                </div>
                <div>
                  <span className="text-gray-500">Antwort wirkt träge:</span>{' '}
                  Lead/Tail runter, Chunk min/max vorsichtig runter.
                </div>
              </div>
            </details>

            {/* Sliders */}
            <div className="space-y-2 pt-2 border-t border-gray-800">
              <div className="text-[10px] font-mono uppercase tracking-wide text-gray-500">
                Gate (Std: min {gateDefaults.current.minThreshold.toFixed(3)}, mult {gateDefaults.current.thresholdMultiplier.toFixed(1)}x, hang {gateDefaults.current.hangoverMs}ms, pre {gateDefaults.current.preRollMs}ms)
              </div>
              <SliderRow label="Min threshold" value={minThreshold} min={0.005} max={0.06} step={0.001}
                display={v => v.toFixed(3)}
                onChange={v => {
                  setMinThreshold(v);
                  configureGate({ minThreshold: v });
                  setSaveState('idle');
                }} />
              <SliderRow label="Multiplier" value={multiplier} min={1.5} max={8} step={0.1}
                display={v => `${v.toFixed(1)}×`}
                onChange={v => {
                  setMultiplier(v);
                  configureGate({ thresholdMultiplier: v });
                  setSaveState('idle');
                }} />
              <SliderRow label="Hangover" value={hangover} min={50} max={1200} step={25}
                display={v => `${v}ms`}
                onChange={v => {
                  const rounded = Math.round(v);
                  setHangover(rounded);
                  configureGate({ hangoverMs: rounded });
                  setSaveState('idle');
                }} />
              <SliderRow label="Pre-roll" value={preRoll} min={0} max={600} step={25}
                display={v => `${v}ms`}
                onChange={v => {
                  const rounded = Math.round(v);
                  setPreRoll(rounded);
                  configureGate({ preRollMs: rounded });
                  setSaveState('idle');
                }} />

              <div className="pt-2 text-[10px] font-mono uppercase tracking-wide text-gray-500">
                Playback (Std: lead {Math.round(playbackDefaults.current.minLeadTimeSeconds * 1000)}ms, tail {Math.round(playbackDefaults.current.pendingEpsilonSeconds * 1000)}ms)
              </div>
              <SliderRow label="Lead buffer" value={playbackLeadMs} min={20} max={200} step={5}
                display={v => `${Math.round(v)}ms`}
                onChange={v => {
                  const ms = Math.round(v);
                  setPlaybackLeadMs(ms);
                  configurePlayback({ minLeadTimeSeconds: ms / 1000 });
                  setSaveState('idle');
                }} />
              <SliderRow label="Tail epsilon" value={playbackTailMs} min={5} max={120} step={5}
                display={v => `${Math.round(v)}ms`}
                onChange={v => {
                  const ms = Math.round(v);
                  setPlaybackTailMs(ms);
                  configurePlayback({ pendingEpsilonSeconds: ms / 1000 });
                  setSaveState('idle');
                }} />

              <div className="pt-2 text-[10px] font-mono uppercase tracking-wide text-gray-500">
                Avatar pacing (Std: min {avatarPacingDefaults.current.minChunkSendMs}ms, max {avatarPacingDefaults.current.maxChunkSendMs}ms, reset {avatarPacingDefaults.current.schedulerResetDriftMs}ms)
              </div>
              <SliderRow label="Chunk min" value={avatarMinChunkMs} min={25} max={180} step={5}
                display={v => `${Math.round(v)}ms`}
                onChange={v => {
                  const nextMin = Math.round(v);
                  const nextMax = Math.max(nextMin, avatarMaxChunkMs);
                  setAvatarMinChunkMs(nextMin);
                  if (nextMax !== avatarMaxChunkMs) {
                    setAvatarMaxChunkMs(nextMax);
                  }
                  configureAvatarPacing({ minChunkSendMs: nextMin, maxChunkSendMs: nextMax });
                  setSaveState('idle');
                }} />
              <SliderRow label="Chunk max" value={avatarMaxChunkMs} min={80} max={300} step={5}
                display={v => `${Math.round(v)}ms`}
                onChange={v => {
                  const nextMax = Math.round(v);
                  const safeMax = Math.max(nextMax, avatarMinChunkMs);
                  setAvatarMaxChunkMs(safeMax);
                  configureAvatarPacing({ maxChunkSendMs: safeMax });
                  setSaveState('idle');
                }} />
              <SliderRow label="Reset drift" value={avatarResetDriftMs} min={100} max={1000} step={25}
                display={v => `${Math.round(v)}ms`}
                onChange={v => {
                  const next = Math.round(v);
                  setAvatarResetDriftMs(next);
                  configureAvatarPacing({ schedulerResetDriftMs: next });
                  setSaveState('idle');
                }} />
            </div>
          </>
        )}

        {activeTab === 'transcript' && (
          <>
            <details className="rounded border border-gray-800 bg-gray-900/70 px-2 py-1" open>
              <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-wide text-gray-400">
                Transkript Guide
              </summary>
              <div className="mt-2 space-y-1 text-[10px] font-mono text-gray-300 leading-relaxed">
                <div>
                  <span className="text-gray-500">Wörter fehlen:</span>{' '}
                  Delay höher (high/xhigh), Min speech leicht runter, Short utterance chars leicht hoch.
                </div>
                <div>
                  <span className="text-gray-500">Zu viele Halluzinationen:</span>{' '}
                  Server-Default far_field behalten; bei Bedarf Max chars/sec runter oder Debounce leicht hoch.
                </div>
                <div>
                  <span className="text-gray-500">Zu aggressives Unterbrechen:</span>{' '}
                  Debounce hoch (z. B. 400-700ms), VAD eagerness auf low/medium.
                </div>
                <div>
                  <span className="text-gray-500">Transkript zu träge:</span>{' '}
                  Delay reduzieren, VAD eagerness höher (nur wenn nötig).
                </div>
              </div>
            </details>

            <div className="space-y-2 pt-2 border-t border-gray-800">
              <div className="text-[10px] font-mono uppercase tracking-wide text-gray-500">
                Realtime STT (Std: delay {transcriptDefaults.current.transcriptionDelay}, noise {transcriptDefaults.current.noiseReductionType}, turn {transcriptDefaults.current.turnDetectionType}, vad {transcriptDefaults.current.vadEagerness})
              </div>
              <div className="px-1 text-[10px] font-mono text-gray-500">
                Model: {getTranscriptTuningConfig().transcriptionModel}
              </div>
              <SelectRow
                label="Delay"
                value={transcriptionDelay}
                options={transcriptDelayOptions}
                onChange={value => {
                  const next = value as TranscriptTuningConfig['transcriptionDelay'];
                  setTranscriptionDelay(next);
                  configureTranscriptTuning({ transcriptionDelay: next });
                  setSaveState('idle');
                }}
              />
              <SelectRow
                label="Noise Red."
                value={noiseReductionType}
                options={noiseReductionOptions}
                onChange={value => {
                  const next = value as TranscriptTuningConfig['noiseReductionType'];
                  setNoiseReductionType(next);
                  configureTranscriptTuning({ noiseReductionType: next });
                  setSaveState('idle');
                }}
              />
              <SelectRow
                label="Turn Detect"
                value={turnDetectionType}
                options={turnDetectionOptions}
                onChange={value => {
                  const next = value as TranscriptTuningConfig['turnDetectionType'];
                  setTurnDetectionType(next);
                  configureTranscriptTuning({ turnDetectionType: next });
                  setSaveState('idle');
                }}
              />
              <SelectRow
                label="VAD Eager"
                value={vadEagerness}
                options={vadEagernessOptions}
                onChange={value => {
                  const next = value as TranscriptTuningConfig['vadEagerness'];
                  setVadEagerness(next);
                  configureTranscriptTuning({ vadEagerness: next });
                  setSaveState('idle');
                }}
              />

              <div className="pt-2 text-[10px] font-mono uppercase tracking-wide text-gray-500">
                Guard Rails (Std: debounce {transcriptDefaults.current.speechInterruptDebounceMs}ms, min speech {transcriptDefaults.current.minValidSpeechMs}ms, short {transcriptDefaults.current.maxShortUtteranceChars}, max cps {transcriptDefaults.current.maxPlausibleCharsPerSec})
              </div>
              <SliderRow label="Debounce" value={speechInterruptDebounceMs} min={0} max={1500} step={25}
                display={v => `${Math.round(v)}ms`}
                onChange={v => {
                  const next = Math.round(v);
                  setSpeechInterruptDebounceMs(next);
                  configureTranscriptTuning({ speechInterruptDebounceMs: next });
                  setSaveState('idle');
                }} />
              <SliderRow label="Min speech" value={minValidSpeechMs} min={50} max={1200} step={10}
                display={v => `${Math.round(v)}ms`}
                onChange={v => {
                  const next = Math.round(v);
                  setMinValidSpeechMs(next);
                  configureTranscriptTuning({ minValidSpeechMs: next });
                  setSaveState('idle');
                }} />
              <SliderRow label="Short chars" value={maxShortUtteranceChars} min={1} max={120} step={1}
                display={v => `${Math.round(v)}`}
                onChange={v => {
                  const next = Math.round(v);
                  setMaxShortUtteranceChars(next);
                  configureTranscriptTuning({ maxShortUtteranceChars: next });
                  setSaveState('idle');
                }} />
              <SliderRow label="Max chars/s" value={maxPlausibleCharsPerSec} min={20} max={200} step={1}
                display={v => `${Math.round(v)}`}
                onChange={v => {
                  const next = Math.round(v);
                  setMaxPlausibleCharsPerSec(next);
                  configureTranscriptTuning({ maxPlausibleCharsPerSec: next });
                  setSaveState('idle');
                }} />
            </div>
          </>
        )}

        {activeTab === 'effective' && (
          <div className="space-y-2">
            <div className="rounded border border-gray-800 bg-gray-900/70 px-2 py-2">
              <div className="mb-2 text-[10px] font-mono uppercase tracking-wide text-gray-500">
                Backend bestaetigt
              </div>
              {effectiveConfig ? (
                <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words text-[10px] leading-relaxed text-gray-300">
                  {effectiveConfigJson}
                </pre>
              ) : (
                <div className="text-[10px] font-mono text-amber-300">
                  Noch keine Backend-Config empfangen. Voice Session starten oder kurz warten.
                </div>
              )}
            </div>
            <button
              onClick={() => {
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(effectiveConfigJson).catch(() => undefined);
                }
              }}
              className="w-full rounded bg-gray-800 py-1 text-xs font-mono font-semibold text-gray-300 transition-colors hover:bg-gray-700"
            >
              CONFIG KOPIEREN
            </button>
          </div>
        )}

        <div className="pt-2 border-t border-gray-800 flex items-center gap-2">
          <button
            onClick={handleSaveSettings}
            className="flex-1 py-1 rounded text-xs font-mono font-semibold bg-emerald-900 text-emerald-200 hover:bg-emerald-800 transition-colors"
          >
            PROFIL SPEICHERN
          </button>
          <button
            onClick={handleSaveLocalSettings}
            className="flex-1 py-1 rounded text-xs font-mono font-semibold bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
          >
            LOKAL
          </button>
          <button
            onClick={handleRestoreDefaults}
            className="flex-1 py-1 rounded text-xs font-mono font-semibold bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
          >
            STANDARD
          </button>
          <span className={`w-16 text-right text-[10px] font-mono ${
            saveState === 'saved'
              ? 'text-emerald-300'
              : saveState === 'failed'
              ? 'text-red-300'
              : saveState === 'saving'
              ? 'text-amber-300'
              : 'text-gray-500'
          }`}>
            {saveState === 'saved' ? 'saved' : saveState === 'saving' ? '...' : saveState === 'failed' ? 'fail' : ' '}
          </span>
        </div>
      </div>
    </div>
  );
};

interface VoiceChatModalProps {
  isOpen: boolean;
  onClose: (messages?: RealtimeMessage[], voiceChatId?: string) => void;
  chatId?: string;
  isDarkMode?: boolean;
  companyName?: string;
  agentName?: string;
  brandIconUrl?: string;
  workspaceSlug?: string;
  agentSlug?: string;
  avatarProfileSlug?: string;
  calendarEnabled?: boolean;
  dashboardUrl?: string;
  publicExitUrl?: string;
  debugMode?: boolean;
  debugProfile?: DebugProfile | null;
  onSaveDebugProfileSettings?: (settings: VoiceDebugSettings) => Promise<boolean> | boolean;
}

export const VoiceChatModal: React.FC<VoiceChatModalProps> = ({
  isOpen,
  onClose,
  chatId,
  isDarkMode = false,
  companyName = 'Synaworks',
  agentName = 'Sally',
  brandIconUrl,
  workspaceSlug,
  agentSlug,
  avatarProfileSlug,
  calendarEnabled = true,
  dashboardUrl,
  publicExitUrl,
  debugMode = false,
  debugProfile = null,
  onSaveDebugProfileSettings,
}) => {
  const avatarVideoRef = useRef<HTMLVideoElement>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [isAvatarReady, setIsAvatarReady] = useState(false);
  const [isAvatarStartupGateOpen, setIsAvatarStartupGateOpen] = useState(false);
  const [isAvatarSpeaking, setIsAvatarSpeaking] = useState(false);
  const [isAvatarMicMuted, setIsAvatarMicMuted] = useState(false);
  const [isUserMicMuted, setIsUserMicMuted] = useState(false);
  const avatarReleaseMuteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCloseRef = useRef<() => void>(() => {});
  const realtimeWasConnectedRef = useRef(false);
  const initialGreetingReleaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialGreetingFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialGreetingBackendActiveRef = useRef(false);
  const initialGreetingSawAvatarSpeechRef = useRef(false);
  const [isInitialGreetingPlaybackLocked, setIsInitialGreetingPlaybackLocked] = useState(false);
  const persistedDebugSettingsRef = useRef<Partial<VoiceDebugSettings> | null>(null);
  const handledVoiceActionIdsRef = useRef<Set<string>>(new Set());
  const handledVoiceActionSignaturesRef = useRef<Set<string>>(new Set());
  const [dismissedPersistentLinkKey, setDismissedPersistentLinkKey] = useState<string | null>(null);
  const [isLinkSidebarOpen, setIsLinkSidebarOpen] = useState(false);

  if (persistedDebugSettingsRef.current === null) {
    persistedDebugSettingsRef.current =
      (debugMode && voiceDebugSettingsFromProfile(debugProfile)) || loadVoiceDebugSettings();
  }

  useEffect(() => {
    if (isOpen) {
      return;
    }
    persistedDebugSettingsRef.current =
      (debugMode && voiceDebugSettingsFromProfile(debugProfile)) || loadVoiceDebugSettings();
  }, [debugMode, debugProfile, isOpen]);

  const persistedDebugSettings = persistedDebugSettingsRef.current || {};
  const persistedGateSettings = persistedDebugSettings.gate;
  const persistedPlaybackSettings = persistedDebugSettings.playback;
  const persistedMinThreshold = persistedGateSettings?.minThreshold;
  const persistedMultiplier = persistedGateSettings?.thresholdMultiplier;
  const persistedHangover = persistedGateSettings?.hangoverMs;
  const persistedPreRoll = persistedGateSettings?.preRollMs;
  const persistedLeadSeconds = persistedPlaybackSettings?.minLeadTimeSeconds;
  const persistedTailSeconds = persistedPlaybackSettings?.pendingEpsilonSeconds;
  const persistedTranscriptSettings = persistedDebugSettings.transcriptTuning;
  const persistedSpeechInterruptDebounceMs = persistedTranscriptSettings?.speechInterruptDebounceMs;
  const persistedMinValidSpeechMs = persistedTranscriptSettings?.minValidSpeechMs;
  const persistedMaxShortUtteranceChars = persistedTranscriptSettings?.maxShortUtteranceChars;
  const persistedMaxPlausibleCharsPerSec = persistedTranscriptSettings?.maxPlausibleCharsPerSec;
  const [voiceInterruptEnabled, setVoiceInterruptEnabled] = useState(() => {
    const savedMuteDuringSpeaking =
      typeof persistedDebugSettings.muteDuringSpeaking === 'boolean'
        ? persistedDebugSettings.muteDuringSpeaking
        : false;
    return !savedMuteDuringSpeaking;
  });

  const initialGateConfig: Partial<GateRuntimeConfig> = {
    enabled:
      typeof persistedGateSettings?.enabled === 'boolean'
        ? persistedGateSettings.enabled
        : DEFAULT_DEBUG_GATE_CONFIG.enabled,
    minThreshold: isFiniteNumber(persistedMinThreshold)
      ? persistedMinThreshold
      : DEFAULT_DEBUG_GATE_CONFIG.minThreshold,
    thresholdMultiplier: isFiniteNumber(persistedMultiplier)
      ? persistedMultiplier
      : DEFAULT_DEBUG_GATE_CONFIG.thresholdMultiplier,
    hangoverMs: isFiniteNumber(persistedHangover)
      ? persistedHangover
      : DEFAULT_DEBUG_GATE_CONFIG.hangoverMs,
    preRollMs: isFiniteNumber(persistedPreRoll)
      ? persistedPreRoll
      : DEFAULT_DEBUG_GATE_CONFIG.preRollMs,
  };

  const initialPlaybackConfig: Partial<AudioPlaybackConfig> = {
    minLeadTimeSeconds: isFiniteNumber(persistedLeadSeconds)
      ? persistedLeadSeconds
      : DEFAULT_DEBUG_PLAYBACK_CONFIG.minLeadTimeSeconds,
    pendingEpsilonSeconds: isFiniteNumber(persistedTailSeconds)
      ? persistedTailSeconds
      : DEFAULT_DEBUG_PLAYBACK_CONFIG.pendingEpsilonSeconds,
  };

  const initialTranscriptTuningConfig: Partial<TranscriptTuningConfig> = {
    transcriptionModel:
      typeof persistedTranscriptSettings?.transcriptionModel === 'string'
        ? persistedTranscriptSettings.transcriptionModel
        : DEFAULT_DEBUG_TRANSCRIPT_TUNING_CONFIG.transcriptionModel,
    transcriptionDelay:
      typeof persistedTranscriptSettings?.transcriptionDelay === 'string'
        ? persistedTranscriptSettings.transcriptionDelay
        : DEFAULT_DEBUG_TRANSCRIPT_TUNING_CONFIG.transcriptionDelay,
    noiseReductionType:
      typeof persistedTranscriptSettings?.noiseReductionType === 'string'
        ? persistedTranscriptSettings.noiseReductionType
        : DEFAULT_DEBUG_TRANSCRIPT_TUNING_CONFIG.noiseReductionType,
    turnDetectionType:
      typeof persistedTranscriptSettings?.turnDetectionType === 'string'
        ? persistedTranscriptSettings.turnDetectionType
        : DEFAULT_DEBUG_TRANSCRIPT_TUNING_CONFIG.turnDetectionType,
    vadEagerness:
      typeof persistedTranscriptSettings?.vadEagerness === 'string'
        ? persistedTranscriptSettings.vadEagerness
        : DEFAULT_DEBUG_TRANSCRIPT_TUNING_CONFIG.vadEagerness,
    speechInterruptDebounceMs: isFiniteNumber(persistedSpeechInterruptDebounceMs)
      ? persistedSpeechInterruptDebounceMs
      : DEFAULT_DEBUG_TRANSCRIPT_TUNING_CONFIG.speechInterruptDebounceMs,
    minValidSpeechMs: isFiniteNumber(persistedMinValidSpeechMs)
      ? persistedMinValidSpeechMs
      : DEFAULT_DEBUG_TRANSCRIPT_TUNING_CONFIG.minValidSpeechMs,
    maxShortUtteranceChars: isFiniteNumber(persistedMaxShortUtteranceChars)
      ? persistedMaxShortUtteranceChars
      : DEFAULT_DEBUG_TRANSCRIPT_TUNING_CONFIG.maxShortUtteranceChars,
    maxPlausibleCharsPerSec: isFiniteNumber(persistedMaxPlausibleCharsPerSec)
      ? persistedMaxPlausibleCharsPerSec
      : DEFAULT_DEBUG_TRANSCRIPT_TUNING_CONFIG.maxPlausibleCharsPerSec,
  };

  const handleAssistantAudioChunk = useCallback((base64Pcm24k: string) => {
    try {
      sendAvatarAudio(base64Pcm24k);
    } catch (err) {
      console.warn('[LiveAvatar] sendAvatarAudio failed:', err);
    }
  }, []);

  const handleAssistantAudioInterrupted = useCallback(() => {
    interruptAvatar();
  }, []);

  const handleVoiceAction = useCallback((voiceAction: RealtimeVoiceAction) => {
    if (!calendarEnabled) {
      return;
    }
    if (voiceAction.action !== 'calendar.open_slot') {
      return;
    }

    if (voiceAction.actionId) {
      if (handledVoiceActionIdsRef.current.has(voiceAction.actionId)) {
        return;
      }
      handledVoiceActionIdsRef.current.add(voiceAction.actionId);
      if (handledVoiceActionIdsRef.current.size > 200) {
        const retained = Array.from(handledVoiceActionIdsRef.current).slice(-120);
        handledVoiceActionIdsRef.current = new Set(retained);
      }
    }

    const payloadRecord =
      voiceAction.payload && typeof voiceAction.payload === 'object'
        ? (voiceAction.payload as Record<string, unknown>)
        : null;
    const schedulingUrl =
      payloadRecord && typeof payloadRecord.schedulingUrl === 'string'
        ? payloadRecord.schedulingUrl
        : null;

    if (!schedulingUrl) {
      return;
    }

    const signature = `${voiceAction.action}:${schedulingUrl}`;
    if (handledVoiceActionSignaturesRef.current.has(signature)) {
      return;
    }
    handledVoiceActionSignaturesRef.current.add(signature);
    if (handledVoiceActionSignaturesRef.current.size > 120) {
      const retained = Array.from(handledVoiceActionSignaturesRef.current).slice(-80);
      handledVoiceActionSignaturesRef.current = new Set(retained);
    }

    window.open(schedulingUrl, '_blank', 'width=800,height=700');
  }, [calendarEnabled]);

  const {
    state,
    isConnected,
    error,
    isMicMuted,
    isInitialGreetingPlaying,
    userTranscript,
    assistantTranscript,
    messages,
    startSession,
    endSession,
    interrupt,
    chatId: voiceChatId,
    showCalendar,
    calendarData,
    closeCalendar,
    getGateStats,
    configureGate,
    getGateConfig,
    getDefaultGateConfig,
    configurePlayback,
    getPlaybackConfig,
    getDefaultPlaybackConfig,
    configureTranscriptTuning,
    getTranscriptTuningConfig,
    getDefaultTranscriptTuningConfig,
    getEffectiveVoiceConfig,
    getMuteDuringSpeaking,
    setMuteDuringSpeaking,
  } = useRealtimeVoice({
    onAssistantAudioChunk: handleAssistantAudioChunk,
    onAssistantAudioInterrupted: handleAssistantAudioInterrupted,
    onVoiceAction: handleVoiceAction,
    playAssistantAudioLocally: !isAvatarReady,
    forceMicMute: isUserMicMuted || isInitialGreetingPlaybackLocked || (!voiceInterruptEnabled && isAvatarMicMuted),
    initialGateConfig,
    initialPlaybackConfig,
    initialTranscriptTuningConfig,
    workspaceSlug,
    agentSlug,
    avatarProfileSlug,
    debugMode,
    debugProfileId: debugMode ? debugProfile?.id : undefined,
    debugProfileSnapshot: debugMode && debugProfile ? (debugProfile as unknown as Record<string, unknown>) : null,
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);
  const hasStartedRef = useRef(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [isCalendarSidebarOpen, setIsCalendarSidebarOpen] = useState(false);
  const effectiveBrandIcon = brandIconUrl || '/favicon-synaworks.png';
  const conversationAgentTitle = `${companyName} - Conversation Agent`;
  const conversationAgentSubtitle = `KI-gestützte virtuelle Ansprechperson ${agentName.trim() || 'Sally'}`;

  const orbState: OrbState =
    state === 'connecting'
      ? 'connecting'
      : state === 'listening' && isMicMuted && !isUserMicMuted
      ? 'thinking'
      : state;

  const clearInitialGreetingPlaybackTimers = useCallback(() => {
    if (initialGreetingReleaseTimerRef.current) {
      clearTimeout(initialGreetingReleaseTimerRef.current);
      initialGreetingReleaseTimerRef.current = null;
    }
    if (initialGreetingFallbackTimerRef.current) {
      clearTimeout(initialGreetingFallbackTimerRef.current);
      initialGreetingFallbackTimerRef.current = null;
    }
  }, []);

  const releaseInitialGreetingPlaybackLock = useCallback(() => {
    clearInitialGreetingPlaybackTimers();
    initialGreetingBackendActiveRef.current = false;
    initialGreetingSawAvatarSpeechRef.current = false;
    setIsInitialGreetingPlaybackLocked(false);
  }, [clearInitialGreetingPlaybackTimers]);

  useEffect(() => {
    if (!isOpen) {
      releaseInitialGreetingPlaybackLock();
      return;
    }

    if (isInitialGreetingPlaying && !initialGreetingBackendActiveRef.current) {
      clearInitialGreetingPlaybackTimers();
      initialGreetingBackendActiveRef.current = true;
      initialGreetingSawAvatarSpeechRef.current = false;
      setIsInitialGreetingPlaybackLocked(true);
      initialGreetingFallbackTimerRef.current = setTimeout(
        releaseInitialGreetingPlaybackLock,
        INITIAL_GREETING_PLAYBACK_LOCK_FALLBACK_MS,
      );
      return;
    }

    if (!isInitialGreetingPlaying) {
      initialGreetingBackendActiveRef.current = false;
    }
  }, [
    isOpen,
    isInitialGreetingPlaying,
    clearInitialGreetingPlaybackTimers,
    releaseInitialGreetingPlaybackLock,
  ]);

  useEffect(() => {
    if (!isOpen || !isInitialGreetingPlaybackLocked) {
      return;
    }

    if (isAvatarSpeaking) {
      initialGreetingSawAvatarSpeechRef.current = true;
      if (initialGreetingReleaseTimerRef.current) {
        clearTimeout(initialGreetingReleaseTimerRef.current);
        initialGreetingReleaseTimerRef.current = null;
      }
      return;
    }

    if (!isInitialGreetingPlaying && initialGreetingSawAvatarSpeechRef.current && !initialGreetingReleaseTimerRef.current) {
      initialGreetingReleaseTimerRef.current = setTimeout(
        releaseInitialGreetingPlaybackLock,
        INITIAL_GREETING_PLAYBACK_RELEASE_MS,
      );
    }
  }, [
    isOpen,
    isInitialGreetingPlaybackLocked,
    isAvatarSpeaking,
    isInitialGreetingPlaying,
    releaseInitialGreetingPlaybackLock,
  ]);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const threshold = 40;
    userScrolledUpRef.current = el.scrollTop + el.clientHeight < el.scrollHeight - threshold;
  }, []);

  const scrollToBottom = useCallback(() => {
    if (userScrolledUpRef.current) return;
    const el = messagesEndRef.current;
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: 'smooth' });
    } catch (err) {
      try {
        el.scrollIntoView();
      } catch (_) {
        // Ignore if scrollIntoView is not supported
      }
    }
  }, []);

  // Scroll on updates
  useEffect(() => {
    scrollToBottom();
  }, [messages, userTranscript, assistantTranscript, scrollToBottom]);

  useEffect(() => {
    const saved = persistedDebugSettingsRef.current || {};

    const savedMuteDuring =
      typeof saved.muteDuringSpeaking === 'boolean' ? saved.muteDuringSpeaking : false;
    setMuteDuringSpeaking(savedMuteDuring);

    const savedPacing = saved.avatarPacing;
    const savedMinChunk = savedPacing?.minChunkSendMs;
    const savedMaxChunk = savedPacing?.maxChunkSendMs;
    const savedResetDrift = savedPacing?.schedulerResetDriftMs;

    const minChunkSendMs = isFiniteNumber(savedMinChunk)
      ? savedMinChunk
      : DEFAULT_DEBUG_AVATAR_PACING_CONFIG.minChunkSendMs;
    const maxChunkSendMs = isFiniteNumber(savedMaxChunk)
      ? savedMaxChunk
      : DEFAULT_DEBUG_AVATAR_PACING_CONFIG.maxChunkSendMs;
    const schedulerResetDriftMs = isFiniteNumber(savedResetDrift)
      ? savedResetDrift
      : DEFAULT_DEBUG_AVATAR_PACING_CONFIG.schedulerResetDriftMs;

    configureAvatarAudioPacing({
      minChunkSendMs,
      maxChunkSendMs,
      schedulerResetDriftMs,
    });
  }, [setMuteDuringSpeaking]);

  // Start the realtime session only after the avatar is visually ready, or after
  // a short fallback gate so the voice experience does not hang on avatar startup.
  useEffect(() => {
    if (isOpen && isAvatarStartupGateOpen && state === 'idle' && !hasStartedRef.current) {
      hasStartedRef.current = true;
      void startSession(chatId).catch((err) => {
        console.warn('[Realtime] startSession failed:', err);
        setAvatarError('Voice-Verbindung konnte nicht gestartet werden. Die Avatar-Session wurde sicher beendet.');
        void stopAvatarSession('openai_start_failed').catch((stopErr) => {
          console.warn('[LiveAvatar] stop after Realtime startup failure failed:', stopErr);
        });
      });
    }
    if (!isOpen) {
      hasStartedRef.current = false;
    }
  }, [isOpen, isAvatarStartupGateOpen, state, startSession, chatId]);

  useEffect(() => {
    if (!isOpen) {
      realtimeWasConnectedRef.current = false;
      return;
    }
    if (isConnected) {
      realtimeWasConnectedRef.current = true;
      return;
    }
    const realtimeFailed = state === 'error';
    const realtimeEndedUnexpectedly = realtimeWasConnectedRef.current && state === 'idle';
    if (!realtimeFailed && !realtimeEndedUnexpectedly) {
      return;
    }
    realtimeWasConnectedRef.current = false;
    const stopReason = realtimeFailed ? 'openai_realtime_error' : 'openai_realtime_disconnected';
    void stopAvatarSession(stopReason).catch((stopErr) => {
      console.warn('[LiveAvatar] stop after Realtime disconnect failed:', stopErr);
    });
  }, [isOpen, isConnected, state]);

  useEffect(() => {
    let cancelled = false;
    let startupGateOpened = false;
    let startupGateTimer: ReturnType<typeof setTimeout> | null = null;

    const clearStartupGateTimer = () => {
      if (!startupGateTimer) {
        return;
      }
      clearTimeout(startupGateTimer);
      startupGateTimer = null;
    };

    const openStartupGate = (reason: string, startedAt: number) => {
      if (cancelled || startupGateOpened) {
        return;
      }
      startupGateOpened = true;
      clearStartupGateTimer();
      console.info('[LiveAvatar] modal startup gate open', {
        reason,
        durationMs: Math.round(getNowMs() - startedAt),
      });
      setIsAvatarStartupGateOpen(true);
    };

    if (!isOpen) {
      setAvatarError(null);
      setIsAvatarReady(false);
      setIsAvatarStartupGateOpen(false);
      return;
    }

    const startedAt = getNowMs();
    setAvatarError(null);
    setIsAvatarReady(false);
    setIsAvatarStartupGateOpen(false);

    startupGateTimer = setTimeout(() => {
      openStartupGate('timeout', startedAt);
    }, AVATAR_STARTUP_GATE_TIMEOUT_MS);

    const startAvatar = async () => {
      try {
        const initialStatus = getAvatarSessionStatus();
        console.info('[LiveAvatar] modal start requested', { initialStatus });
        await startAvatarSession({
          chat_id: chatId,
          customer_slug: workspaceSlug,
          workspace_slug: 'main',
          agent_slug: agentSlug,
          avatar_profile_slug: avatarProfileSlug,
          entrypoint: 'voice_modal',
          is_prewarm: false,
        });

        if (cancelled) {
          await stopAvatarSession();
          return;
        }

        let firstFrameReady = false;
        if (avatarVideoRef.current) {
          attachAvatar(avatarVideoRef.current);
          firstFrameReady = await waitForAvatarFirstFrame(
            avatarVideoRef.current,
            AVATAR_FIRST_FRAME_WAIT_MS,
          );
        }

        if (cancelled) {
          return;
        }

        console.info('[LiveAvatar] modal avatar ready', {
          initialStatus,
          firstFrameReady,
          durationMs: Math.round(getNowMs() - startedAt),
        });
        if (!firstFrameReady) {
          console.warn('[LiveAvatar] modal first video frame not confirmed before voice start');
        }
        setIsAvatarReady(true);
        openStartupGate(firstFrameReady ? 'first_frame' : 'session_ready_no_frame', startedAt);
      } catch (err) {
        if (cancelled) {
          return;
        }
        setAvatarError(getAvatarStartupErrorMessage(err));
        setIsAvatarReady(false);
        openStartupGate('avatar_error', startedAt);
      }
    };

    void startAvatar();

    return () => {
      cancelled = true;
      clearStartupGateTimer();
      void stopAvatarSession().catch((err) => {
        console.warn('[LiveAvatar] stopAvatarSession failed:', err);
      });
      setIsAvatarReady(false);
      setIsAvatarStartupGateOpen(false);
    };
  }, [isOpen, chatId, workspaceSlug, agentSlug, avatarProfileSlug]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setAvatarTrackingContext({
      chat_id: voiceChatId || chatId,
      customer_slug: workspaceSlug,
      workspace_slug: 'main',
      agent_slug: agentSlug,
      avatar_profile_slug: avatarProfileSlug,
      entrypoint: 'voice_modal',
      is_prewarm: false,
    });
  }, [isOpen, voiceChatId, chatId, workspaceSlug, agentSlug, avatarProfileSlug]);

  useEffect(() => {
    const clearReleaseTimer = () => {
      if (!avatarReleaseMuteTimerRef.current) {
        return;
      }
      clearTimeout(avatarReleaseMuteTimerRef.current);
      avatarReleaseMuteTimerRef.current = null;
    };

    if (!isOpen) {
      clearReleaseTimer();
      setIsAvatarSpeaking(false);
      setIsAvatarMicMuted(false);
      setIsUserMicMuted(false);
      return;
    }

    const unsubscribe = subscribeAvatarSpeakingState((isSpeaking) => {
      setIsAvatarSpeaking(isSpeaking);
      clearReleaseTimer();
      if (isSpeaking) {
        setIsAvatarMicMuted(true);
        return;
      }

      avatarReleaseMuteTimerRef.current = setTimeout(() => {
        setIsAvatarMicMuted(false);
        avatarReleaseMuteTimerRef.current = null;
      }, AVATAR_MIC_RELEASE_DELAY_MS);
    });

    return () => {
      unsubscribe();
      clearReleaseTimer();
      setIsAvatarSpeaking(false);
      setIsAvatarMicMuted(false);
    };
  }, [isOpen]);

  // Handle close
  const handleClose = useCallback(() => {
    // Pass all finalized messages plus any live transcripts
    const finalMessages: RealtimeMessage[] = [...messages];

    // Check if there's a live user transcript not in messages
    if (userTranscript.trim()) {
      const normalizedUserTranscript = normalizeTranscriptForComparison(userTranscript);
      const alreadySaved = messages.some(
        m => m.role === 'user' && normalizeTranscriptForComparison(m.content) === normalizedUserTranscript
      );
      if (!alreadySaved) {
        finalMessages.push({ role: 'user', content: userTranscript });
      }
    }

    // Check if there's a live assistant transcript not in messages
    if (assistantTranscript.trim()) {
      const normalizedAssistantTranscript = normalizeTranscriptForComparison(assistantTranscript);
      const alreadySaved = messages.some(
        m => m.role === 'assistant' && normalizeTranscriptForComparison(m.content) === normalizedAssistantTranscript
      );
      if (!alreadySaved) {
        finalMessages.push({ role: 'assistant', content: assistantTranscript });
      }
    }

    void stopAvatarSession().catch((err) => {
      console.warn('[LiveAvatar] stopAvatarSession failed:', err);
    });
    endSession();
    onClose(finalMessages.length > 0 ? finalMessages : undefined, voiceChatId || undefined);
  }, [endSession, onClose, messages, userTranscript, assistantTranscript, voiceChatId]);
  handleCloseRef.current = handleClose;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const timeout = setTimeout(() => {
      console.warn('[VoiceChat] 30-minute session timeout reached; closing voice session');
      handleCloseRef.current();
    }, MAX_VOICE_SESSION_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  const handleBackToDashboard = useCallback(() => {
    handleClose();
    if (dashboardUrl) {
      window.setTimeout(() => {
        window.location.assign(dashboardUrl);
      }, 0);
    }
  }, [dashboardUrl, handleClose]);

  const handleBackToPublicExit = useCallback(() => {
    handleClose();
    if (publicExitUrl) {
      window.setTimeout(() => {
        window.location.assign(publicExitUrl);
      }, 0);
    }
  }, [handleClose, publicExitUrl]);

  const handleInterrupt = useCallback(() => {
    if (isInitialGreetingPlaying || isInitialGreetingPlaybackLocked) {
      return;
    }
    interrupt();
    interruptAvatar();
  }, [interrupt, isInitialGreetingPlaying, isInitialGreetingPlaybackLocked]);

  const canInterrupt =
    !isInitialGreetingPlaying &&
    !isInitialGreetingPlaybackLocked &&
    (state === 'speaking' || isAvatarSpeaking);
  const voiceHeaderConnectionState = state === 'error'
    ? 'error'
    : isConnected
      ? 'connected'
      : 'connecting';

  const handleOrbClick = useCallback(() => {
    if (canInterrupt) {
      handleInterrupt();
      return;
    }

    if (state === 'listening') {
      setIsUserMicMuted(prev => !prev);
    }
  }, [state, canInterrupt, handleInterrupt]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === ' ' && canInterrupt) {
        e.preventDefault();
        handleInterrupt();
      } else if (debugMode && (e.key === 'p' || e.key === 'P')) {
        setShowDebugPanel(prev => !prev);
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleClose, canInterrupt, handleInterrupt, debugMode]);

  useEffect(() => {
    if (!isOpen) {
      setIsCalendarSidebarOpen(false);
      setDismissedPersistentLinkKey(null);
      setIsLinkSidebarOpen(false);
      return;
    }
    if (!calendarEnabled) {
      setIsCalendarSidebarOpen(false);
    } else if (showCalendar) {
      setIsCalendarSidebarOpen(true);
      setIsLinkSidebarOpen(false);
    }
  }, [calendarEnabled, isOpen, showCalendar]);

  if (!isOpen) return null;

  const debugPanel = debugMode && showDebugPanel && (
    <VoiceDebugPanel
      getGateStats={getGateStats}
      configureGate={configureGate}
      getGateConfig={getGateConfig}
      getDefaultGateConfig={getDefaultGateConfig}
      configurePlayback={configurePlayback}
      getPlaybackConfig={getPlaybackConfig}
      getDefaultPlaybackConfig={getDefaultPlaybackConfig}
      configureTranscriptTuning={configureTranscriptTuning}
      getTranscriptTuningConfig={getTranscriptTuningConfig}
      getDefaultTranscriptTuningConfig={getDefaultTranscriptTuningConfig}
      getEffectiveVoiceConfig={getEffectiveVoiceConfig}
      configureAvatarPacing={configureAvatarAudioPacing}
      getAvatarPacing={getAvatarAudioPacingConfig}
      getDefaultAvatarPacing={getDefaultAvatarAudioPacingConfig}
      getMuteDuringSpeaking={getMuteDuringSpeaking}
      setMuteDuringSpeaking={setMuteDuringSpeaking}
      onVoiceInterruptEnabledChange={setVoiceInterruptEnabled}
      debugProfileName={debugProfile?.name}
      onSaveDebugProfileSettings={onSaveDebugProfileSettings}
      onClose={() => setShowDebugPanel(false)}
    />
  );
  const getStatusText = () => {
    if (state === 'connecting') return 'Verbinde...';
    if (isInitialGreetingPlaying) return null;
    if (state === 'listening' && isUserMicMuted) return 'Mikrofon ist stummgeschaltet';
    if (state === 'listening' && isMicMuted) return 'Warte, bis die Wiedergabe fertig ist...';
    if (state === 'listening' && !userTranscript) return 'Ich höre zu... Sprechen Sie';
    if (state === 'thinking') return 'Denke nach...';
    if (canInterrupt) return null;
    return null;
  };

  const statusText = getStatusText();
  const hasPrefetchedCalendarData = Boolean(calendarData);
  const shouldRenderCalendarWidget = calendarEnabled && (showCalendar || hasPrefetchedCalendarData);

  // Check if current live transcripts are already in messages
  const normalizedUserTranscript = normalizeTranscriptForComparison(userTranscript);
  const normalizedAssistantTranscript = normalizeTranscriptForComparison(assistantTranscript);
  const userAlreadySaved = messages.some(
    m => m.role === 'user' && normalizeTranscriptForComparison(m.content) === normalizedUserTranscript
  );
  const assistantAlreadySaved = messages.some(
    m => m.role === 'assistant' && normalizeTranscriptForComparison(m.content) === normalizedAssistantTranscript
  );

  // Show live transcripts only if they're not already saved and not empty
  const showLiveUser = userTranscript.trim() && !userAlreadySaved;
  const showLiveAssistant = assistantTranscript.trim() && !assistantAlreadySaved;
  const linkSidebarItems = messages.reduce<Array<{ key: string; url: string; label: string }>>((items, msg, index) => {
    if (msg.role === 'link' && typeof msg.linkUrl === 'string') {
      const url = msg.linkUrl.trim();
      if (url) {
        items.push({
          key: `${index}:${url}`,
          url,
          label: getLinkCtaLabel(msg.linkLabel),
        });
      }
    }
    return items;
  }, []);
  const latestLinkItem = linkSidebarItems[linkSidebarItems.length - 1];
  const latestLinkUrl = latestLinkItem?.url || '';
  const latestLinkLabel = latestLinkItem?.label || 'Link';
  const latestLinkKey = latestLinkItem?.key || '';
  const linkSidebarTitle = linkSidebarItems.length > 1 ? `${linkSidebarItems.length} Links verfügbar` : 'Link verfügbar';
  const showPersistentLinkCta = Boolean(latestLinkUrl && dismissedPersistentLinkKey !== latestLinkKey);
  const showLinkSidebar = showPersistentLinkCta && isLinkSidebarOpen;
  const showDesktopSidebar = (calendarEnabled && isCalendarSidebarOpen) || showLinkSidebar;
  const desktopSidebarTogglePosition = showDesktopSidebar ? 'right-3 lg:right-[432px]' : 'right-3';
  const renderPersistentLinkCta = (visibilityClassName: string) => (
    <div className={`voice-talk-persistent-link relative z-[60] shrink-0 border-t ${visibilityClassName} ${isDarkMode ? 'border-[#ed7203]/30 bg-[#1a1a1a]' : 'border-[#f4d2b3] bg-white'}`}>
      <div className="px-3 py-2.5 sm:px-6">
        <div className={`voice-talk-persistent-link-card mx-auto flex min-w-0 max-w-4xl items-center gap-2 rounded-lg border px-3 py-2 shadow-sm ${
            isDarkMode
              ? 'border-[#ed7203]/30 bg-[#ed7203]/15 text-orange-50'
              : 'border-[#f4d2b3] bg-[#fff3e8] text-[#3a2614]'
          }`}>
            <a
              href={latestLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-w-0 flex-1 items-center gap-2 no-underline"
            >
              <ExternalLink className={`h-4 w-4 shrink-0 ${isDarkMode ? 'text-orange-300' : 'text-[#c75f02]'}`} />
              <span className="truncate text-sm font-semibold">{latestLinkLabel} öffnen</span>
            </a>
            <button
              type="button"
              onClick={() => setDismissedPersistentLinkKey(latestLinkKey)}
              className={`shrink-0 rounded-full p-1.5 transition-colors ${
                isDarkMode ? 'text-orange-200 hover:bg-white/10' : 'text-[#c75f02] hover:bg-[#fde6cf]'
              }`}
              aria-label="Link ausblenden"
              title="Link ausblenden"
            >
              <X className="h-4 w-4" />
            </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
    {debugPanel}
    <div className={`voice-talk-modal fixed inset-0 z-50 flex flex-col overflow-x-hidden overflow-y-hidden ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-gradient-to-b from-white to-[#f1f2f2]'}`}>
      {/* Header */}
      <div className={`voice-talk-header relative z-10 flex min-w-0 items-center justify-between gap-3 p-4 border-b ${isDarkMode ? 'bg-[#1a1a1a] border-[#3d3d3d]' : 'bg-white border-[#e6e6e7]'}`}>
        <ConversationHeaderBrand
          isDarkMode={isDarkMode}
          title={conversationAgentTitle}
          subtitle={conversationAgentSubtitle}
          brandIconSrc={effectiveBrandIcon}
          brandIconAlt={companyName}
          onHomeClick={publicExitUrl ? handleBackToPublicExit : dashboardUrl ? handleBackToDashboard : handleClose}
          connectionState={voiceHeaderConnectionState}
        />
        <div className="voice-talk-header-actions flex shrink-0 items-center gap-2">
          {publicExitUrl ? (
            <button
              type="button"
              onClick={handleBackToPublicExit}
              className={`voice-talk-public-exit-button inline-flex h-10 items-center gap-2 rounded-full border px-3 text-sm font-semibold transition-colors ${
                isDarkMode
                  ? 'border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800'
                  : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Zur Synaworks.ai"
              title="Zur Synaworks.ai"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Synaworks.ai</span>
            </button>
          ) : dashboardUrl ? (
            <button
              type="button"
              onClick={handleBackToDashboard}
              className={`voice-talk-dashboard-button inline-flex h-10 items-center gap-2 rounded-full border px-3 text-sm font-semibold transition-colors ${
                isDarkMode
                  ? 'border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800'
                  : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Zurück zum Dashboard"
              title="Zurück zum Dashboard"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </button>
          ) : null}
          {debugMode && (
            <button
              type="button"
              onClick={() => setShowDebugPanel(prev => !prev)}
              className={`voice-talk-debug-button inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors ${
                isDarkMode
                  ? 'border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800'
                  : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Voice Debug Panel öffnen"
              title="Voice Debug Panel"
            >
              <SlidersHorizontal className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="voice-talk-main relative flex flex-1 min-h-0 min-w-0 flex-col lg:flex-row">
        <div className="voice-talk-content flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="voice-talk-avatar-section min-h-0 flex-1 px-4 py-4 sm:px-6 lg:pb-10">
            <div className="voice-talk-avatar-stage mx-auto flex h-full w-full max-w-4xl items-center justify-center">
              <div className="voice-talk-avatar-frame relative aspect-video w-full max-h-full">
                <div className={`voice-talk-avatar-video-shell h-full w-full overflow-hidden rounded-2xl border ${
                  isDarkMode ? 'border-[#3d3d3d] bg-black' : 'border-[#e6e6e7] bg-black'
                }`}>
                  <video
                    ref={avatarVideoRef}
                    className="h-full w-full object-cover"
                    autoPlay
                    playsInline
                  />
                  {(avatarError || !isAvatarReady) && (
                    <div className="voice-talk-avatar-status pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent px-4 py-3 sm:px-5">
                      <div className="text-xs sm:text-sm text-white/90">
                        {avatarError ? `Avatar offline: ${avatarError}` : 'Avatar wird verbunden...'}
                      </div>
                    </div>
                  )}
                </div>

                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 hidden translate-y-1/2 justify-center lg:flex">
                  <div className="voice-talk-desktop-controls pointer-events-auto flex min-w-0 items-center gap-3">
                    <VoiceOrb
                      state={orbState}
                      size="sm"
                      onClick={handleOrbClick}
                      isMicMuted={isUserMicMuted}
                    />
                    <button
                      onClick={handleInterrupt}
                      disabled={!canInterrupt}
                      className={`voice-talk-interrupt-button h-10 w-10 shrink-0 rounded-full text-white shadow-xl transition-all duration-200 flex items-center justify-center ${
                        canInterrupt
                          ? 'bg-amber-500 hover:scale-105 hover:bg-amber-600 active:scale-95'
                          : 'cursor-not-allowed bg-gray-500 opacity-70'
                      }`}
                      aria-label="Avatar unterbrechen"
                      title="Avatar unterbrechen"
                    >
                      <Square className="h-5 w-5" />
                    </button>
                    <button
                      onClick={handleClose}
                      className="voice-talk-end-button min-w-0 flex items-center gap-2 rounded-full bg-red-500 px-4 py-2.5 text-sm text-white shadow-xl transition-all duration-200 hover:scale-105 hover:bg-red-600 active:scale-95"
                    >
                      <PhoneOff className="h-5 w-5" />
                      <span className="truncate font-medium">Gespräch beenden</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`voice-talk-transcript-section shrink-0 border-t ${isDarkMode ? 'border-[#3d3d3d] bg-[#1a1a1a]/90' : 'border-[#e6e6e7] bg-white/95'}`}>
            <div className="voice-talk-transcript-inner px-4 py-3 sm:px-6">
              <div className="voice-talk-transcript-layout mx-auto flex h-40 w-full max-w-4xl min-w-0 items-stretch sm:h-48">
                <div
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  className={`voice-talk-transcript-panel min-w-0 flex-1 overflow-y-auto rounded-xl border ${
                  isDarkMode ? 'border-[#3d3d3d] bg-black/25' : 'border-[#e6e6e7] bg-[#f7f7f7]'
                }`}>
                  <div className="space-y-3 p-3 sm:p-4">
                    {/* Hint shown before the first user message */}
                    {messages.filter(m => m.role === 'user').length === 0 && (
                      <div className={`voice-talk-hint rounded-xl p-3 text-center text-sm ${isDarkMode ? 'bg-white/5 text-slate-400' : 'bg-white text-slate-500'}`}>
                        Beginnen Sie das Gespräch, indem sie sprechen. Sprechen Sie laut und deutlich, und reduzieren Sie Hintergrundgeräusche so weit wie möglich.
                      </div>
                    )}

                    {/* All finalized messages in order */}
                    {messages.map((msg, index) => {
                      if (msg.role === 'link') {
                        return (
                          <a
                            key={`link-${index}`}
                            href={msg.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`voice-talk-link-card group flex items-center gap-3 rounded-xl border-l-4 border-[#ed7203] p-3 no-underline transition-all hover:-translate-y-0.5 hover:shadow-md ${
                              isDarkMode
                                ? 'bg-[#ed7203]/15 text-orange-100 hover:bg-[#ed7203]/20'
                                : 'bg-[#fff3e8] text-[#3a2614] hover:bg-[#fde6cf]'
                            }`}
                          >
                            <div className="flex min-w-0 flex-1 flex-col">
                              <span className="text-sm font-semibold leading-snug">{msg.linkLabel}</span>
                              <span className={`mt-0.5 truncate text-xs ${isDarkMode ? 'text-orange-300' : 'text-[#c75f02]'}`}>
                                {msg.linkUrl}
                              </span>
                            </div>
                            <svg className={`h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${isDarkMode ? 'text-orange-300' : 'text-[#c75f02]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        );
                      }
                      return (
                        <div
                          key={`${msg.role}-${index}`}
                          className={`voice-talk-message ${msg.role === 'user' ? 'voice-talk-message-user' : 'voice-talk-message-assistant'} rounded-xl p-3 ${
                            msg.role === 'user'
                              ? isDarkMode ? 'bg-[#ed7203]/20 text-orange-100' : 'bg-[#fff3e8] text-[#3a2614]'
                              : isDarkMode ? 'bg-white/10 text-slate-100' : 'bg-white text-[#1a1a1a]'
                          }`}
                        >
                          <div className="mb-1 text-xs font-medium uppercase opacity-60">
                            {msg.role === 'user' ? 'Sie' : 'Assistent'}
                          </div>
                          <div className="text-sm leading-relaxed sm:text-base">{msg.content}</div>
                        </div>
                      );
                    })}

                    {/* Live user transcript (while speaking) */}
                    {showLiveUser && (
                      <div className={`voice-talk-message voice-talk-message-user rounded-xl p-3 ${isDarkMode ? 'bg-[#ed7203]/20 text-orange-100' : 'bg-[#fff3e8] text-[#3a2614]'}`}>
                        <div className="mb-1 text-xs font-medium uppercase opacity-60">Sie</div>
                        <div className="text-sm leading-relaxed sm:text-base">
                          {userTranscript}
                          {state === 'listening' && <span className="voice-talk-live-cursor ml-1 inline-block h-4 w-2 animate-pulse rounded bg-[#ed7203]" />}
                        </div>
                      </div>
                    )}

                    {/* Live assistant transcript (while speaking) */}
                    {showLiveAssistant && (
                      <div className={`voice-talk-message voice-talk-message-assistant rounded-xl p-3 ${isDarkMode ? 'bg-white/10 text-slate-100' : 'bg-white text-[#1a1a1a]'}`}>
                        <div className="mb-1 text-xs font-medium uppercase opacity-60">Assistent</div>
                        <div className="text-sm leading-relaxed sm:text-base">
                          {assistantTranscript}
                          {state === 'speaking' && <span className="voice-talk-live-cursor ml-1 inline-block h-4 w-2 animate-pulse rounded bg-[#ed7203]" />}
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {calendarEnabled && (
          <button
            type="button"
            onClick={() => {
              setIsCalendarSidebarOpen(prev => {
                const next = !prev;
                if (next) setIsLinkSidebarOpen(false);
                return next;
              });
            }}
            className={`voice-talk-calendar-toggle absolute ${desktopSidebarTogglePosition} top-1/2 z-40 hidden -translate-y-1/2 items-center gap-1 rounded-full border px-3 py-2 text-xs font-semibold shadow-lg transition-colors lg:flex ${
              isDarkMode
                ? 'border-gray-700 bg-gray-900/95 text-gray-200 hover:bg-gray-800'
                : 'border-gray-300 bg-white/95 text-gray-700 hover:bg-gray-100'
            }`}
            aria-label={isCalendarSidebarOpen ? 'Kalender einklappen' : 'Kalender ausklappen'}
            title={isCalendarSidebarOpen ? 'Kalender einklappen' : 'Kalender ausklappen'}
          >
            <Calendar className="h-4 w-4" />
            {isCalendarSidebarOpen ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        )}

        {showPersistentLinkCta && (
          <button
            type="button"
            onClick={() => {
              setIsLinkSidebarOpen(prev => {
                const next = !prev;
                if (next) setIsCalendarSidebarOpen(false);
                return next;
              });
            }}
            className={`voice-talk-link-toggle absolute ${desktopSidebarTogglePosition} top-[calc(50%+3.25rem)] z-40 hidden -translate-y-1/2 items-center gap-1 rounded-full border px-3 py-2 text-xs font-semibold shadow-lg transition-colors lg:flex ${
              isDarkMode
                ? 'border-[#ed7203]/40 bg-gray-900/95 text-orange-100 hover:bg-gray-800'
                : 'border-[#f4d2b3] bg-white/95 text-[#9a4d04] hover:bg-[#fff3e8]'
            }`}
            aria-label={isLinkSidebarOpen ? 'Link einklappen' : 'Link anzeigen'}
            title={isLinkSidebarOpen ? 'Link einklappen' : 'Link anzeigen'}
          >
            <ExternalLink className="h-4 w-4" />
            <span>{linkSidebarItems.length > 1 ? `${linkSidebarItems.length} Links` : 'Link verfügbar'}</span>
            {isLinkSidebarOpen ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        )}

        {(calendarEnabled || showPersistentLinkCta) && (
          <div className={`relative hidden lg:block lg:min-h-0 lg:shrink-0 lg:transition-[width] lg:duration-300 lg:ease-out ${
            showDesktopSidebar ? 'lg:w-[420px]' : 'lg:w-0'
          }`}>
            <div className={`voice-talk-calendar-sidebar absolute inset-y-0 right-0 flex min-h-0 w-[420px] flex-col border-l transition-transform duration-300 ease-out ${
              isDarkMode ? 'bg-[#1a1a1a] border-[#3d3d3d]' : 'bg-white border-[#e6e6e7]'
            } ${showDesktopSidebar ? 'translate-x-0' : 'translate-x-full'}`}>
              {isCalendarSidebarOpen && shouldRenderCalendarWidget ? (
                <div className="min-h-0 flex-1 overflow-hidden">
                  <CalendarWidget
                    isDarkMode={isDarkMode}
                    onClose={() => {
                      closeCalendar();
                      setIsCalendarSidebarOpen(false);
                    }}
                    calendarData={calendarData}
                    layout="embedded"
                  />
                </div>
              ) : showLinkSidebar ? (
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className={`shrink-0 border-b px-5 py-4 ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                    <div className="flex min-w-0 items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-orange-300' : 'text-[#c75f02]'}`}>{linkSidebarTitle}</div>
                        <div className={`mt-1 truncate text-sm font-semibold ${isDarkMode ? 'text-gray-100' : 'text-[#1a1a1a]'}`}>
                          {linkSidebarItems.length > 1 ? `Zuletzt: ${latestLinkLabel}` : latestLinkLabel}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsLinkSidebarOpen(false)}
                        className={`shrink-0 rounded-full p-2 transition-colors ${
                          isDarkMode ? 'text-gray-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        aria-label="Link einklappen"
                        title="Link einklappen"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-5">
                    {linkSidebarItems.map(item => (
                      <a
                        key={item.key}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`voice-talk-link-card group flex min-w-0 items-start gap-3 rounded-xl border-l-4 border-[#ed7203] p-4 no-underline transition-all hover:-translate-y-0.5 hover:shadow-md ${
                          isDarkMode
                            ? 'bg-[#ed7203]/15 text-orange-100 hover:bg-[#ed7203]/20'
                            : 'bg-[#fff3e8] text-[#3a2614] hover:bg-[#fde6cf]'
                        }`}
                      >
                        <ExternalLink className={`mt-0.5 h-4 w-4 shrink-0 ${isDarkMode ? 'text-orange-300' : 'text-[#c75f02]'}`} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold leading-snug">{item.label} öffnen</span>
                          <span className={`mt-1 block break-all text-xs leading-relaxed ${isDarkMode ? 'text-orange-300' : 'text-[#c75f02]'}`}>
                            {item.url}
                          </span>
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={`flex min-h-0 flex-1 items-center justify-center px-8 text-center text-sm ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-500'
                }`}>
                  Kalender erscheint hier, sobald ein Termin vorgeschlagen wird.
                </div>
              )}

              {(statusText || error) && (
                <div className={`shrink-0 border-t px-4 py-3 ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                  {statusText && (
                    <div className={`text-center text-sm ${state === 'listening' && !userTranscript ? 'animate-pulse' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {statusText}
                    </div>
                  )}

                  {error && (
                    <div className={`rounded-lg bg-red-500/20 px-4 py-2 text-center text-sm text-red-500 ${statusText ? 'mt-3' : ''}`}>
                      {error}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {calendarEnabled && showCalendar && (
        <CalendarWidget
          isDarkMode={isDarkMode}
          onClose={closeCalendar}
          calendarData={calendarData}
          containerClassName={`lg:hidden inset-x-0 top-[73px] ${showPersistentLinkCta ? 'bottom-[178px]' : 'bottom-[112px]'}`}
        />
      )}

      {showPersistentLinkCta && (
        renderPersistentLinkCta('lg:hidden')
      )}

      <div className={`voice-talk-mobile-controls relative z-[60] shrink-0 border-t lg:hidden ${isDarkMode ? 'border-[#3d3d3d] bg-[#1a1a1a]' : 'border-[#e6e6e7] bg-white'}`}>
        <div className="px-3 py-3 sm:px-6 sm:py-4">
          <div className="flex min-w-0 items-center justify-center gap-3 sm:gap-6">
            <VoiceOrb
              state={orbState}
              size="sm"
              onClick={handleOrbClick}
              isMicMuted={isUserMicMuted}
            />
            <button
              onClick={handleInterrupt}
              disabled={!canInterrupt}
              className={`voice-talk-interrupt-button h-10 w-10 sm:h-12 sm:w-12 shrink-0 rounded-full text-white transition-all duration-200 shadow-lg flex items-center justify-center ${
                canInterrupt
                  ? 'bg-amber-500 hover:scale-105 hover:bg-amber-600 active:scale-95'
                  : 'cursor-not-allowed bg-gray-500 opacity-70'
              }`}
              aria-label="Avatar unterbrechen"
              title="Avatar unterbrechen"
            >
              <Square className="w-5 h-5" />
            </button>
            <button
              onClick={handleClose}
              className="voice-talk-end-button min-w-0 flex items-center gap-2 px-4 py-2.5 rounded-full bg-red-500 hover:bg-red-600 text-white transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg text-sm sm:gap-3 sm:px-5 sm:py-3 sm:text-base"
            >
              <PhoneOff className="w-5 h-5" />
              <span className="truncate font-medium">Gespräch beenden</span>
            </button>
          </div>

          {statusText && (
            <div className={`mt-3 text-center text-sm ${state === 'listening' && !userTranscript ? 'animate-pulse' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {statusText}
            </div>
          )}

          {error && (
            <div className="mt-3 px-4 py-2 bg-red-500/20 text-red-500 rounded-lg text-sm text-center">
              {error}
            </div>
          )}
        </div>
        <div className={`hidden sm:block border-t ${isDarkMode ? 'border-gray-700/80' : 'border-gray-200/80'}`} />
      </div>
    </div>
    </>
  );
};

export default VoiceChatModal;
